import { useState, type ReactElement } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import type { LandingType } from '../../../hooks/usePlusWebsites';
import { useCreatePage } from '../hooks/useCreatePage';
import { HOME_PAGE } from '../standardPages';
import { AddPageModal } from './AddPageModal';
import { SwitchLandingTypeButton } from './SwitchLandingTypeButton';

interface HomePageCardProps {
  websiteId: string;
  landingType: LandingType;
  externalHomepageUrl: string | null | undefined;
}

// Shown while there is no in-app home page row to attach the controls to: either
// the website uses an external home page, or the builder has no home page yet.
// Once the builder has one, its row carries the switch instead.
export function HomePageCard({
  websiteId,
  landingType,
  externalHomepageUrl,
}: HomePageCardProps): ReactElement {
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const [isAddHomePageModalOpen, setIsAddHomePageModalOpen] = useState(false);
  const createHomePage = useCreatePage();
  const isExternal = landingType === 'external';
  const description = isExternal
    ? 'You are using your own home page'
    : 'The page builder has no home page yet';

  return (
    <Box className="vtwo:flex vtwo:justify-between vtwo:items-center vtwo:px-6 vtwo:py-5 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white">
      <Box>
        <Typography className="vtwo:font-bold">Home Page</Typography>
        <Typography variant="body2" className="vtwo:text-gray-500">
          {description}
        </Typography>
        {isExternal && externalHomepageUrl && (
          <Typography
            component="a"
            href={externalHomepageUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            className="vtwo:text-blue-600 vtwo:underline"
          >
            {externalHomepageUrl}
          </Typography>
        )}
      </Box>
      <Box className="vtwo:flex vtwo:items-center vtwo:gap-3">
        {!isExternal && (
          <Button
            variant="outlined"
            disabled={createHomePage.isLoading}
            onClick={() => setIsAddHomePageModalOpen(true)}
            className="vtwo:rounded-lg vtwo:text-featured vtwo:border-featured vtwo:hover:border-featured-hover vtwo:hover:bg-featured/[0.04]"
          >
            Add home page
          </Button>
        )}
        <SwitchLandingTypeButton
          websiteId={websiteId}
          landingType={landingType}
          externalHomepageUrl={externalHomepageUrl}
        />
      </Box>

      {isAddHomePageModalOpen && (
        <AddPageModal
          open
          pageName={HOME_PAGE.name}
          organisationId={organisationId}
          isSaving={createHomePage.isLoading}
          onClose={() => setIsAddHomePageModalOpen(false)}
          onCreate={(seo) =>
            createHomePage.mutate(
              { websiteId, name: HOME_PAGE.name, href: HOME_PAGE.href, seo },
              { onSuccess: () => setIsAddHomePageModalOpen(false) }
            )
          }
        />
      )}
    </Box>
  );
}
